import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories, shoppingGuideLinks } from '@/mocks/categories';

export default function Navbar() {
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileExpandedSection, setMobileExpandedSection] = useState<string | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const categoryRef = useRef<HTMLDivElement>(null);
  const guideRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (categoryRef.current && !categoryRef.current.contains(e.target as Node)) {
        setIsCategoryOpen(false);
      }
      if (guideRef.current && !guideRef.current.contains(e.target as Node)) {
        setIsGuideOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleMobileSection = (section: string) => {
    setMobileExpandedSection(mobileExpandedSection === section ? null : section);
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-background-50/95 backdrop-blur-sm border-b border-background-200/50' : 'bg-transparent'}`}>
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 cursor-pointer">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500">
              <i className="ri-table-line text-background-50 text-lg"></i>
            </div>
            <span className="font-heading font-semibold text-lg text-foreground-900 hidden sm:block">
              Vigorbuy Spreadsheet Hub
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-5">
            <Link
              to="/"
              className="flex items-center gap-1.5 text-sm font-medium text-foreground-700 hover:text-foreground-900 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-line"></i>
              <span>Home</span>
            </Link>

            <div className="relative" ref={categoryRef}>
              <button
                onClick={() => { setIsCategoryOpen(!isCategoryOpen); setIsGuideOpen(false); }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-foreground-700 hover:text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-grid-line"></i>
                <span>Categories</span>
                <i className={`ri-arrow-down-s-line transition-transform duration-200 text-xs ${isCategoryOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {isCategoryOpen && (
                <div className="absolute top-full mt-2 left-0 w-64 bg-background-50 rounded-xl border border-background-200/70 py-2 max-h-[420px] overflow-y-auto shadow-lg">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer"
                      onClick={() => setIsCategoryOpen(false)}
                    >
                      <div className="w-7 h-7 flex items-center justify-center rounded-md bg-accent-100">
                        <i className={`${cat.icon} text-accent-600 text-sm`}></i>
                      </div>
                      <span>{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <div className="relative" ref={guideRef}>
              <button
                onClick={() => { setIsGuideOpen(!isGuideOpen); setIsCategoryOpen(false); }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-foreground-700 hover:text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-book-read-line"></i>
                <span>Shopping Guide</span>
                <i className={`ri-arrow-down-s-line transition-transform duration-200 text-xs ${isGuideOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {isGuideOpen && (
                <div className="absolute top-full mt-2 left-0 w-56 bg-background-50 rounded-xl border border-background-200/70 py-2 shadow-lg">
                  {shoppingGuideLinks.map((guide) => (
                    <Link
                      key={guide.name}
                      to={guide.link}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer"
                      onClick={() => setIsGuideOpen(false)}
                    >
                      <div className="w-7 h-7 flex items-center justify-center rounded-md bg-primary-50">
                        <i className={`${guide.icon} text-primary-600 text-sm`}></i>
                      </div>
                      <span>{guide.name}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Visit Main Website
            </a>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-100 cursor-pointer"
          >
            <i className={`text-xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-background-200/70 max-h-[calc(100vh-4rem)] overflow-y-auto">
            <div className="py-3 space-y-1">
              <Link
                to="/"
                className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 cursor-pointer"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100">
                  <i className="ri-home-line text-accent-600"></i>
                </div>
                <span>Home</span>
              </Link>
            </div>

            <div className="border-t border-background-200/50">
              <button
                onClick={() => toggleMobileSection('categories')}
                className="w-full flex items-center justify-between px-3 py-3 cursor-pointer hover:bg-background-100/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100">
                    <i className="ri-grid-line text-accent-600"></i>
                  </div>
                  <span className="text-sm font-semibold text-foreground-900">Product Categories</span>
                </div>
                <i className={`ri-arrow-down-s-line text-foreground-500 transition-transform duration-200 ${mobileExpandedSection === 'categories' ? 'rotate-180' : ''}`}></i>
              </button>
              {mobileExpandedSection === 'categories' && (
                <div className="pb-2 space-y-0.5 px-2">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-600 hover:text-foreground-900 hover:bg-background-100 cursor-pointer"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <div className="w-7 h-7 flex items-center justify-center rounded-md bg-accent-50">
                        <i className={`${cat.icon} text-accent-600 text-xs`}></i>
                      </div>
                      <span>{cat.name}</span>
                      <i className="ri-external-link-line text-[10px] text-foreground-400 ml-auto"></i>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <div className="border-t border-background-200/50">
              <button
                onClick={() => toggleMobileSection('guides')}
                className="w-full flex items-center justify-between px-3 py-3 cursor-pointer hover:bg-background-100/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-50">
                    <i className="ri-book-read-line text-primary-600"></i>
                  </div>
                  <span className="text-sm font-semibold text-foreground-900">Shopping Guides</span>
                </div>
                <i className={`ri-arrow-down-s-line text-foreground-500 transition-transform duration-200 ${mobileExpandedSection === 'guides' ? 'rotate-180' : ''}`}></i>
              </button>
              {mobileExpandedSection === 'guides' && (
                <div className="pb-2 space-y-0.5 px-2">
                  {shoppingGuideLinks.map((guide) => (
                    <Link
                      key={guide.name}
                      to={guide.link}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-600 hover:text-foreground-900 hover:bg-background-100 cursor-pointer"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <div className="w-7 h-7 flex items-center justify-center rounded-md bg-primary-50">
                        <i className={`${guide.icon} text-primary-600 text-xs`}></i>
                      </div>
                      <span>{guide.name}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <div className="border-t border-background-200/50 p-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 w-full py-3 rounded-lg bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors cursor-pointer"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <i className="ri-external-link-line"></i>
                Visit Main Website
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}